import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { 
  FiFileText, FiSave, FiUploadCloud, FiPlus, FiEdit2, 
  FiTrash2, FiCalendar, FiChevronLeft, FiChevronRight, FiX, FiEye 
} from "react-icons/fi";
import ApiServices from "../../services/apiservices";
import { toast } from "sonner";

const FinancialStatement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");

  // Modal Context Management State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  const dynamicYearsRange = ["All", "2024", "2025", "2026", "2027"];
  const monthsList = [
    { value: "01", name: "January" }, { value: "02", name: "February" },
    { value: "03", name: "March" }, { value: "04", name: "April" },
    { value: "05", name: "May" }, { value: "06", name: "June" },
    { value: "07", name: "July" }, { value: "08", name: "August" },
    { value: "09", name: "September" }, { value: "10", name: "October" },
    { value: "11", name: "November" }, { value: "12", name: "December" }
  ];

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const endpoint = `/financial-statements/fetch.php?page=${page}&limit=10&year=${selectedYear}&month=${selectedMonth}`;
      const res = await ApiServices.getRequest(endpoint);
      if (res && res.success) {
        setOrders(res.orders || []);
        setTotalPages(res.totalPages || 1);
      }
    } catch (error) {
      toast.error("Failed to fetch orders list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, selectedYear, selectedMonth]);

  const openCreateModal = () => {
    setEditingOrder(null);
    setIsModalOpen(true);
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this regulatory order?")) return;
    try {
      const res = await ApiServices.deleteRequest(`/financial-statements/delete.php?id=${id}`);
      if (res.success) {
        toast.success(res.message || "Order removed successfully");
        fetchOrders();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message || "Error processing delete event loop");
    }
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    pdfFile: Yup.mixed().nested === false ? null : Yup.mixed().test(
      "fileType", 
      "Only PDF Document files are supported", 
      (value) => !value || (value && value.type === "application/pdf")
    ).test("requiredOnCreate", "An official PDF document file configuration is required", function(value) {
      return editingOrder ? true : !!value;
    }),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      if (values.pdfFile) {
        formData.append("pdfFile", values.pdfFile);
      }
      let res;
      if (editingOrder) {

        res = await ApiServices.putRequest(`/financial-statements/update.php?id=${editingOrder.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await ApiServices.postRequest("/financial-statements/create.php", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.success) {
        toast.success(res.message || "Operation completed successfully");
        setIsModalOpen(false);
        resetForm();
        fetchOrders();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message || "An runtime error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 font-sans py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        
        {/* Header Action Module */}
        <div className="mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-center md:text-left">
            <span className="text-xs font-black text-green-700 uppercase tracking-widest block mb-2">Admin Dashboard</span>
            <h1 className="text-3xl md:text-5xl font-black text-black uppercase tracking-tighter mb-2">Financial Statement</h1>
            <div className="w-20 h-1.5 bg-yellow-400 rounded-full mx-auto md:mx-0"></div>
          </div>
          <button
            onClick={openCreateModal}
            className="bg-green-700 text-white px-6 py-3.5 rounded-xl hover:bg-black transition-all flex items-center justify-center gap-2 font-black uppercase tracking-widest text-xs shadow-md self-center md:self-auto"
          >
            <FiPlus size={16} />New Statement
          </button>
        </div>

        {/* Filter Framework Module */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-8 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-700 flex-shrink-0">
            <FiCalendar className="text-green-700" size={18} /> System Filters:
          </div>
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:flex sm:items-center">
            <select
              value={selectedYear}
              onChange={(e) => { setSelectedYear(e.target.value); setPage(1); }}
              className="px-4 py-2 border rounded-xl text-sm font-semibold bg-gray-50 outline-none focus:ring-2 focus:ring-green-700"
            >
              <option value="All">All Years</option>
              {dynamicYearsRange.filter(y => y !== "All").map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            <select
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setPage(1); }}
              className="px-4 py-2 border rounded-xl text-sm font-semibold bg-gray-50 outline-none focus:ring-2 focus:ring-green-700"
            >
              <option value="All">All Months</option>
              {monthsList.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
            </select>
          </div>
        </div>

        {/* Table Management Viewport */}
        {loading ? (
          <div className="w-full py-20 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-green-700 border-t-yellow-400 rounded-full animate-spin"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-3xl border p-12 text-center shadow-sm">
            <p className="text-gray-400 font-medium">No official records found matching your active filter criteria.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-wider">Document Specs</th>
                    <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-wider hidden md:table-cell">Uploaded Date</th>
                    <th className="p-4 text-xs font-black uppercase text-gray-400 tracking-wider text-right">System Management</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-700 flex-shrink-0">
                            <FiFileText size={20} />
                          </div>
                          <span className="font-bold text-black text-sm uppercase tracking-tight max-w-xs md:max-w-md block truncate">
                            {order.title}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider hidden md:table-cell">
                        {new Date(order.createdAt).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' })}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <a 
                            href={order.pdfFileUrl} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-black hover:text-white transition-all"
                            title="Preview File"
                          >
                            <FiEye size={16} />
                          </a>
                          <button
                            onClick={() => openEditModal(order)}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all"
                            title="Edit Document Meta"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all"
                            title="Purge Document Matrix"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Unified Pagination Infrastructure */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => setPage(p => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="p-2.5 bg-white border rounded-xl disabled:opacity-40 hover:bg-gray-100 transition shadow-sm"
            >
              <FiChevronLeft size={18} />
            </button>
            <span className="text-xs font-black text-gray-500 uppercase tracking-widest">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
              className="p-2.5 bg-white border rounded-xl disabled:opacity-40 hover:bg-gray-100 transition shadow-sm"
            >
              <FiChevronRight size={18} />
            </button>
          </div>
        )}

        {/* Document Action Modal Component */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100 p-6 md:p-8 relative max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
              
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-black rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>

              <h2 className="text-xl md:text-2xl font-black text-black uppercase tracking-tight mb-1">
                {editingOrder ? "Modify Document Parameters" : "Upload Financial Statement"}
              </h2>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-6">
                {editingOrder ? "Refreshing document structural identity metadata mappings" : "Publish financial statement"}
              </p>

              <Formik 
                initialValues={{ title: editingOrder ? editingOrder.title : "", pdfFile: null }} 
                validationSchema={validationSchema} 
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, isSubmitting, values }) => (
                  <Form className="space-y-5">
                    <div>
                      <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-2 block">Title</label>
                      <Field 
                        name="title" 
                        placeholder="e.g BEDC Audited Financial Statement 20.." 
                        className="w-full border rounded-xl px-4 py-3.5 text-sm font-semibold focus:ring-2 focus:ring-green-700 outline-none bg-gray-50/50 transition-all" 
                      />
                      <ErrorMessage name="title" component="p" className="text-red-500 text-xs mt-1 font-bold pl-1" />
                    </div>

                    <div>
                      <label className="text-xs font-black uppercase tracking-wider text-gray-500 mb-2 block">
                        <FiFileText className="inline mr-1" /> Document Stream Context {editingOrder && "(Optional)"}
                      </label>
                      
                      <div className="relative border-2 border-dashed border-gray-200 hover:border-green-700 transition-colors rounded-2xl bg-gray-50/30 p-6 text-center flex flex-col items-center justify-center cursor-pointer">
                        <input 
                          type="file" 
                          accept="application/pdf" 
                          onChange={(e) => setFieldValue("pdfFile", e.currentTarget.files[0])} 
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        />
                        <FiUploadCloud className="text-gray-400 text-3xl mb-2" />
                        <p className="text-xs font-bold text-black max-w-[200px] truncate">
                          {values.pdfFile ? values.pdfFile.name : "Click to override tracking binary content"}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-1 font-medium">Accepts binary application/pdf targets</p>
                      </div>
                      <ErrorMessage name="pdfFile" component="p" className="text-red-500 text-xs mt-1 font-bold pl-1" />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(false)}
                        className="px-5 py-3 text-xs font-black uppercase tracking-widest text-gray-500 hover:bg-gray-100 rounded-xl transition-all"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        disabled={isSubmitting} 
                        className="bg-green-700 text-white px-6 py-3 rounded-xl hover:bg-black transition-all disabled:bg-gray-400 flex items-center justify-center gap-2 font-black uppercase tracking-widest text-xs shadow-md"
                      >
                        <FiSave size={14} /> {isSubmitting ? "Processing Content..." : editingOrder ? "Save Workspace" : "Publish Document"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FinancialStatement;